/**
 * Rising guess bubbles overlay for the Who Am I? round scene.
 *
 * Each registered chat guess (correct OR wrong) spawns a small navy pill at
 * the bottom of the round content area; the pill rises ~300px over ~3s and
 * fades during the last 1s. Mirrors the Survey Says / Anagram bubble pattern
 * but lives inside the 1180px content budget so it never overlaps the
 * right-side leaderboard.
 *
 * Limits:
 *   - Max 30 concurrent bubbles. Oldest gets dropped if we'd exceed.
 *   - Truncate guess text to 16 chars + "..." if longer.
 *
 * No user IDs / display names are rendered — see commit 1fc84bd.
 */

export interface GuessBubblesHandle {
  el: HTMLDivElement;
  spawn(guess: string): void;
  clear(): void;
}

const STYLES = `
  @keyframes wai-bubble-rise {
    0%   { opacity: 0;    transform: translate(-50%, 0)      scale(0.85); }
    12%  { opacity: 0.95; transform: translate(-50%, -40px)  scale(1.0); }
    66%  { opacity: 0.95; transform: translate(-50%, -240px) scale(1.0); }
    100% { opacity: 0;    transform: translate(-50%, -300px) scale(0.95); }
  }
  .wai-bubbles-wrap {
    position: absolute;
    /* Sit at the bottom of the 1180px content area (left:80px content start,
       right:1500px is where the side LB begins → reserve 740px on the right
       so bubbles never overlap the leaderboard). */
    left: 80px;
    right: 740px;
    bottom: 0;
    height: 360px;
    pointer-events: none;
    overflow: visible;
    z-index: 32;
  }
  .wai-bubble {
    position: absolute;
    bottom: 0;
    left: var(--wai-bubble-x, 50%);
    transform: translate(-50%, 0);
    padding: 10px 18px;
    min-width: 80px;
    max-width: 220px;
    background: rgba(6, 12, 233, 0.78);
    border: 2px solid rgba(0, 240, 255, 0.6);
    border-radius: 999px;
    color: #FFFFFF;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 600;
    font-size: 18px;
    letter-spacing: 0.04em;
    text-shadow: 0 1px 2px rgba(0,0,0,0.45);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45), 0 0 18px rgba(0, 240, 255, 0.25);
    white-space: nowrap;
    animation: wai-bubble-rise 3000ms cubic-bezier(0.22, 0.62, 0.4, 1.0) both;
    will-change: transform, opacity;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('wai-bubble-style')) return;
  const s = document.createElement('style');
  s.id = 'wai-bubble-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

const MAX_BUBBLES = 30;
const BUBBLE_LIFE_MS = 3000;
const TRUNCATE_AT = 16;

function truncate(s: string): string {
  const trimmed = s.trim();
  if (trimmed.length <= TRUNCATE_AT) return trimmed;
  return trimmed.slice(0, TRUNCATE_AT) + '...';
}

export function createGuessBubbles(stage: HTMLElement): GuessBubblesHandle {
  ensureStyles();
  const wrap = document.createElement('div');
  wrap.className = 'wai-bubbles-wrap';
  wrap.setAttribute('data-testid', 'wai-guess-bubbles');
  stage.appendChild(wrap);

  const live: HTMLDivElement[] = [];

  function spawn(guess: string): void {
    const text = truncate(guess);
    if (!text) return;
    // Drop oldest if we're at the cap.
    while (live.length >= MAX_BUBBLES) {
      const dead = live.shift();
      dead?.remove();
    }
    const b = document.createElement('div');
    b.className = 'wai-bubble';
    b.setAttribute('data-testid', 'wai-guess-bubble');
    b.textContent = text;
    // Spread bubbles horizontally inside the content area (10..90% so the
    // pill never clips the LB-clear margin). Random per spawn so a chat
    // firehose looks lively.
    const xPct = 10 + Math.random() * 80;
    b.style.setProperty('--wai-bubble-x', `${xPct.toFixed(2)}%`);
    wrap.appendChild(b);
    live.push(b);
    setTimeout(() => {
      b.remove();
      const idx = live.indexOf(b);
      if (idx >= 0) live.splice(idx, 1);
    }, BUBBLE_LIFE_MS + 60);
  }

  function clear(): void {
    while (live.length > 0) {
      const b = live.pop();
      b?.remove();
    }
  }

  return { el: wrap, spawn, clear };
}
